import React, { useState } from "react";
import {
  Box,
  AppBar,
  Toolbar as MuiToolbar,
  Typography,
  Button,
  IconButton,
  Menu,
  MenuItem,
  TextField,
  Divider,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import {
  Save as SaveIcon,
  Undo as UndoIcon,
  Redo as RedoIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  ContentCopy as CopyIcon,
  ContentPaste as PasteIcon,
} from "@mui/icons-material";

const Toolbar = ({
  onSave,
  onExport,
  onUndo,
  onRedo,
  canUndo,
  canRedo,
  onNewForm,
  onDeleteForm,
  formName,
  onFormNameChange,
  onAddPage,
  totalPages,
  currentPageIndex,
  extraRight,
  extraLeft,
  onCopyComponent,
  onPasteComponent,
  canCopy,
  canPaste,
  saving = false,
}) => {
  const [exportAnchorEl, setExportAnchorEl] = useState(null);
  const [isEditingName, setIsEditingName] = useState(false);
  const [tempFormName, setTempFormName] = useState(formName);

  const handleExportClose = () => {
    setExportAnchorEl(null);
  };

  const handleExportSelect = (format) => {
    onExport(format);
    handleExportClose();
  };

  const handleNameEdit = () => {
    setIsEditingName(true);
    setTempFormName(formName);
  };

  const handleNameSave = () => {
    onFormNameChange(tempFormName);
    setIsEditingName(false);
  };

  const handleNameCancel = () => {
    setTempFormName(formName);
    setIsEditingName(false);
  };

  const handleNameKeyPress = (e) => {
    if (e.key === "Enter") {
      handleNameSave();
    } else if (e.key === "Escape") {
      handleNameCancel();
    }
  };

  return (
    <AppBar
      position="static"
      elevation={1}
      sx={{
        bgcolor: "background.paper",
        color: "text.primary",
        borderBottom: "1px solid",
        borderColor: "divider",
      }}
    >
      <MuiToolbar sx={{ minHeight: "64px !important" }}>
        {/* Elementos extra izquierda */}
        {extraLeft && <Box sx={{ mr: 2 }}>{extraLeft}</Box>}

        {/* Nombre del formulario */}
        <Box sx={{ flex: 1, display: "flex", alignItems: "center" }}>
          {isEditingName ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <TextField
                value={tempFormName}
                onChange={(e) => setTempFormName(e.target.value)}
                onKeyDown={handleNameKeyPress}
                size="small"
                autoFocus
                sx={{ minWidth: 200 }}
              />
              <IconButton size="small" onClick={handleNameSave} color="primary">
                <CheckIcon />
              </IconButton>
              <IconButton size="small" onClick={handleNameCancel}>
                <CloseIcon />
              </IconButton>
            </Box>
          ) : (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                {formName}
              </Typography>
              <IconButton size="small" onClick={handleNameEdit}>
                <EditIcon fontSize="small" />
              </IconButton>
            </Box>
          )}
        </Box>

        {/* Botones de acción */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {/* Información de páginas */}
          <Typography variant="body2" color="text.secondary" sx={{ mr: 2 }}>
            Página {currentPageIndex + 1} de {totalPages}
          </Typography>

          {/* Agregar página */}
          <Tooltip title="Agregar nueva página (F2)">
            <IconButton onClick={onAddPage} color="primary">
              <AddIcon />
            </IconButton>
          </Tooltip>

          <Divider orientation="vertical" flexItem />

          {/* Copiar/Pegar */}
          <Tooltip title="Copiar componente (Ctrl+C)">
            <span>
              <IconButton onClick={onCopyComponent} disabled={!canCopy}>
                <CopyIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Pegar componente (Ctrl+V)">
            <span>
              <IconButton onClick={onPasteComponent} disabled={!canPaste}>
                <PasteIcon />
              </IconButton>
            </span>
          </Tooltip>

          <Divider orientation="vertical" flexItem />

          {/* Deshacer/Rehacer */}
          <Tooltip title="Deshacer (Ctrl+Z)">
            <span>
              <IconButton onClick={onUndo} disabled={!canUndo}>
                <UndoIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Rehacer (Ctrl+Y / Ctrl+Shift+Z)">
            <span>
              <IconButton onClick={onRedo} disabled={!canRedo}>
                <RedoIcon />
              </IconButton>
            </span>
          </Tooltip>

          <Divider orientation="vertical" flexItem />

          {/* Vista previa */}
          {/* <Tooltip title="Vista previa">
            <Button
              variant="outlined"
              startIcon={<PreviewIcon />}
              onClick={onPreview}
              size="small"
            >
              Vista previa
            </Button>
          </Tooltip> */}

          {/* Exportar */}
          {/* <Tooltip title="Exportar formulario">
            <Button
              variant="outlined"
              startIcon={<ExportIcon />}
              onClick={handleExportClick}
              size="small"
            >
              Exportar
            </Button>
          </Tooltip> */}

          <Menu anchorEl={exportAnchorEl} open={Boolean(exportAnchorEl)} onClose={handleExportClose}>
            <MenuItem onClick={() => handleExportSelect("json")}>Exportar como JSON</MenuItem>
            <MenuItem onClick={() => handleExportSelect("pdf")}>Exportar como PDF</MenuItem>
          </Menu>

          <Divider orientation="vertical" flexItem />

          {/* Guardar */}
          <Tooltip title="Guardar formulario (Ctrl+S)">
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
              onClick={onSave}
              disabled={saving}
              size="small"
              sx={{ minWidth: 100 }}
            >
              {saving ? "Guardando..." : "Guardar"}
            </Button>
          </Tooltip>

          {/* Elementos extra derecha */}
          {extraRight && <Box sx={{ ml: 1 }}>{extraRight}</Box>}
        </Box>
      </MuiToolbar>
    </AppBar>
  );
};

export default Toolbar;
